import { COMPANY_EMAIL, COMPANY_EMAIL_MAILTO } from "../../constants/company";

type TermsBulletGroup = {
  heading?: string;
  items: string[];
};

type TermsSection = {
  title: string;
  paragraphs?: string[];
  bulletGroups?: TermsBulletGroup[];
};

export const TERMS_LAST_UPDATED = "July 2026";

const termsSections: TermsSection[] = [
  {
    title: "Agreement to Terms",
    paragraphs: [
      "Welcome to MyTeenGig. We connect teens with safe work opportunities where parent or guardian supervision is required for teen users ages 14-17. Teen users ages 18-19 do not need a parent or guardian profile linked to their account.",
      "By using MyTeenGig, you agree to these Terms of Service and our Privacy Policy.",
    ],
  },
  {
    title: "Age Requirements and Parent/Guardian Consent",
    paragraphs: [
      "MyTeenGig is for teens ages 14-19 only. Parent or guardian consent is required for teen users ages 14-17.",
    ],
    bulletGroups: [
      {
        items: [
          "Teen users ages 14-17 must have verified parent or guardian consent for each accepted gig. The teen and parent or guardian accounts must be linked.",
          "Parents and guardians must supervise their teen's activity and approve gig acceptances from their own profile.",
          "Job posters must be 18 or older.",
          "Users under 14 are not allowed on the platform.",
        ],
      },
    ],
  },
  {
    title: "Account Registration and Verification",
    bulletGroups: [
      {
        heading: "Teen users",
        items: [
          "A parent or guardian must create their own profile and link it to the teen profile through an account invite.",
          "Parental consent and profile validation are required for teen users ages 14-17.",
          "Teen users ages 14-17 may chat with posters only with the required parent or guardian approval.",
          "Parents and guardians can monitor their teen's profile, gig activity, and platform messaging.",
        ],
      },
      {
        heading: "Job posters",
        items: [
          "Job posters must be 18 or older.",
          "Profile validation is required.",
          "Business license verification may be required where applicable.",
          "Job posters must commit to a teen-safe work environment.",
        ],
      },
    ],
  },
  {
    title: "Acceptable Use Policy",
    bulletGroups: [
      {
        heading: "You agree not to",
        items: [
          "Post gigs that are illegal, unsafe, or inappropriate for teens.",
          "Try to bypass parent or guardian controls, consent, or supervision.",
          "Use gig descriptions or standard gig-request messages to exchange personal contact details before the gig is confirmed.",
          "Harass, discriminate against, threaten, or behave inappropriately toward others.",
          "Misrepresent your age, identity, qualifications, or gig details.",
          "Arrange in-person meetings without required parent or guardian approval and appropriate safety steps.",
        ],
      },
      {
        heading: "Gigs should be",
        items: [
          "Age-appropriate opportunities such as tutoring, pet care, yard work, errands, event help, or similar safe work.",
          "In safe environments with adult supervision where appropriate.",
          "Compliant with local child labor laws and all applicable rules.",
          "Free from hazardous, high-risk, or inappropriate conditions.",
        ],
      },
    ],
  },
  {
    title: "Personal Information and Contact Details",
    paragraphs: [
      "In gig descriptions and standard gig-request conversations, users must not share phone numbers, email addresses, social media handles, home addresses, or similar personal contact information before the gig is confirmed.",
      "Keep pre-confirmation gig communication on MyTeenGig so parent or guardian oversight and platform safety controls can be used. Confirmed scheduled gigs and paid business listing application messaging may include contact details when needed to coordinate next steps, but users remain responsible for sharing only what is appropriate.",
    ],
  },
  {
    title: "Safety and Security Measures",
    bulletGroups: [
      {
        heading: "Platform safety features",
        items: [
          "Verified user profiles.",
          "Secure platform messaging.",
          "Parent and guardian oversight tools.",
        ],
      },
      {
        heading: "User responsibilities",
        items: [
          "Follow all safety guidelines.",
          "Report suspicious activity or anyone attempting to bypass platform rules.",
          "Decline or leave any gig that feels unsafe or inappropriate.",
          "Use secure platform communication methods.",
          "Verify that a gig is legitimate before accepting it.",
        ],
      },
    ],
  },
  {
    title: "Payment Terms and Fees",
    paragraphs: [
      "Platform fees help cover third-party services used to operate MyTeenGig, including location features, hosting, email, messaging, and related tools. Fees are calculated from the total amount paid for each gig.",
      "Payments are processed through Stripe's secure payment system. Parents and guardians approve gig acceptance for teen users ages 14-17 and may help manage their teen's earnings where applicable.",
      "Payment disputes are handled fairly, with parent or guardian involvement when a minor teen is involved.",
    ],
  },
  {
    title: "Privacy and Data Protection",
    paragraphs: [
      "Teen privacy is a top priority. We collect only the information needed to support safety, account verification, payments, and the core features of the platform.",
    ],
    bulletGroups: [
      {
        items: [
          "Parents and guardians have access to their teen's profile and gig activity where applicable.",
          "Data is stored and transmitted securely.",
          "Users may request that their data be deleted at any time.",
          "When an account is deleted, direct profile and contact details are removed or de-identified where practical, but MyTeenGig may retain limited deletion-audit identity details such as first name and email, plus historical gig, review, payment, dispute, messaging, safety, and audit records when needed to operate the platform, support users, comply with legal or financial obligations, resolve disputes, prevent misuse, or protect teen safety.",
        ],
      },
    ],
  },
  {
    title: "Platform Rules and Community Standards",
    paragraphs: [
      "MyTeenGig has a zero tolerance policy for conduct that may put a teen at risk or violate our safety standards. If a profile is flagged for conduct that breaches these policies, the account may be suspended immediately and law enforcement may be contacted when appropriate.",
    ],
    bulletGroups: [
      {
        heading: "Prohibited activities",
        items: [
          "Trying to bypass parent or guardian controls.",
          "Sharing or requesting phone numbers, email addresses, social media handles, home addresses, or similar private contact details in gig descriptions or standard gig-request conversations before the gig is confirmed.",
          "Sending inappropriate messages or behaving inappropriately.",
          "Posting fake, misleading, unsafe, or inappropriate gig listings.",
          "Discriminating against anyone for any reason.",
        ],
      },
      {
        heading: "Enforcement actions may include",
        items: [
          "Warnings or account restrictions.",
          "Temporary suspension.",
          "Permanent removal from the platform.",
          "Reporting to authorities when needed.",
        ],
      },
    ],
  },
  {
    title: "Liability and Disclaimers",
    paragraphs: [
      "MyTeenGig uses safety measures to support responsible platform use, but we cannot catch every issue. Users, parents, guardians, and job posters are responsible for making careful decisions before accepting or posting a gig.",
      "MyTeenGig helps connect users with work opportunities we call gigs. We do not guarantee specific outcomes, earnings, gig availability, or user conduct.",
    ],
    bulletGroups: [
      {
        items: [
          "Users should verify that a gig is safe and legitimate before accepting it.",
          "If you are uncomfortable or feel unsafe at a gig, leave immediately, contact the appropriate authorities if needed, and report the situation to MyTeenGig.",
          "Parents and guardians are ultimately responsible for their teen's safety and supervision.",
          "MyTeenGig's liability is limited to the maximum extent allowed by law.",
        ],
      },
    ],
  },
  {
    title: "Changes to These Terms",
    paragraphs: [
      "We may update these Terms when our services change, laws are updated, or community feedback helps us improve the platform.",
    ],
    bulletGroups: [
      {
        items: [
          "Updated Terms will be made available on this website.",
          "Material changes go into effect 30 days after the updated Terms are posted.",
          "Continuing to use MyTeenGig after changes take effect means you accept the updated Terms.",
        ],
      },
    ],
  },
];

type TermsContentProps = {
  className?: string;
};

const TermsContent = ({ className = "" }: TermsContentProps) => (
  <div className={`space-y-6 text-left text-sm leading-relaxed text-myteengig-text-muted ${className}`.trim()}>
    <div className="rounded-lg border border-myteengig-border bg-myteengig-background p-4">
      <p className="text-base font-semibold text-myteengig-text-primary">
        Detailed guidelines for responsible and safe use of MyTeenGig
      </p>
      <p className="mt-2 text-sm font-medium text-myteengig-text-muted">Last Updated: {TERMS_LAST_UPDATED}</p>
    </div>

    <div className="space-y-5">
      {termsSections.map((section) => (
        <section key={section.title} className="border-b border-myteengig-border pb-5 last:border-b-0 last:pb-0">
          <h3 className="text-lg font-semibold text-myteengig-text-primary">{section.title}</h3>
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph} className="mt-3">
              {paragraph}
            </p>
          ))}
          {section.bulletGroups?.map((group) => (
            <div key={`${section.title}-${group.heading ?? group.items[0]}`} className="mt-4">
              {group.heading ? (
                <h4 className="text-sm font-semibold uppercase tracking-wide text-myteengig-text-primary">{group.heading}</h4>
              ) : null}
              <ul className="mt-2 list-disc space-y-2 pl-5">
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}

      <section className="border-b-0 pb-0">
        <h3 className="text-lg font-semibold text-myteengig-text-primary">Contact Us</h3>
        <p className="mt-3">
          Have questions about these Terms or need help understanding a policy? Contact us for general inquiries and safety concerns at{" "}
          <a href={COMPANY_EMAIL_MAILTO} className="font-semibold text-myteengig-primary underline underline-offset-2">
            {COMPANY_EMAIL}
          </a>
          .
        </p>
      </section>
    </div>
  </div>
);

export default TermsContent;
